/**
 * Shared document panels for Attachments, Terms & Conditions, and Notes.
 * Used by both Requisitions and Purchase Orders.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import TypeAheadLookup from '../TypeAheadLookup';
import { EmptyState } from '../layout/SharedComponents';
import { Upload, Trash2, Download, Plus, X, FileText, ScrollText, StickyNote, Save } from 'lucide-react';
import { useConfirm } from '../ConfirmDialog';

// ── Attachments Panel ───────────────────────────────────────────────────────

export function AttachmentsPanel({ docType, docNumber, canEdit, onReload, fmtTz }) {
  const confirm = useConfirm();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [desc, setDesc] = useState('');

  const basePath = docType === 'orders' ? `/api/orders/${docNumber}` : `/api/requisitions/${docNumber}`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${basePath}/attachments`);
      setAttachments(Array.isArray(r.data) ? r.data : r.data?.attachments || []);
    } catch (e) { console.error("Panel load failed:", e?.response?.status, e?.message); }
    setLoading(false);
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('description', desc);
    try {
      await api.post(`${basePath}/attachments`, fd);
      toast.success('File uploaded');
      setDesc('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('ring-2', 'ring-primary', 'bg-primary/5');
    const files = Array.from(e.dataTransfer?.files || []);
    files.forEach(f => handleUpload(f));
  };

  const handleDelete = async (att) => {
    if (!await confirm('Delete this attachment?', { title: 'Delete Attachment', destructive: true })) return;
    try {
      const deleteKey = docType === 'orders' ? encodeURIComponent(att.safe_name || att.file_name) : (att.id || att.file_name);
      await api.delete(`${basePath}/attachments/${deleteKey}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <h3 className="font-bold text-primary mb-3">Attached Documents</h3>
      {canEdit && (
        <div
          onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-primary', 'bg-primary/5'); }}
          onDragLeave={e => e.currentTarget.classList.remove('ring-2', 'ring-primary', 'bg-primary/5')}
          onDrop={handleDrop}
          className="border-2 border-dashed rounded-lg p-4 mb-3 text-center transition-colors"
        >
          <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">Drag & drop files here</p>
          <div className="flex items-center gap-2 mt-2 justify-center">
            <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)" className="h-7 text-xs max-w-[200px]" />
            <input type="file" id={`attach-${docType}`} className="hidden" onChange={e => { handleUpload(e.target.files?.[0]); e.target.value=''; }} />
            <Button size="sm" variant="outline" onClick={() => document.getElementById(`attach-${docType}`)?.click()}>
              <Upload className="h-3.5 w-3.5 mr-1" /> Browse
            </Button>
          </div>
        </div>
      )}
      {loading ? <p className="text-xs text-muted-foreground">Loading...</p> : attachments.length === 0 ? (
        <EmptyState title="No documents attached" />
      ) : (
        <table className="w-full text-xs">
          <thead><tr className="border-b bg-muted/50">
            <th className="text-left px-3 py-2">File Name</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-left px-3 py-2">Uploaded By</th>
            <th className="text-left px-3 py-2">Date</th>
            <th className="w-16"></th>
          </tr></thead>
          <tbody>
            {attachments.map((a, i) => (
              <tr key={i} className="border-b hover:bg-accent/20">
                <td className="px-3 py-2 flex items-center gap-1"><FileText className="h-3.5 w-3.5 text-primary" />{a.file_name}</td>
                <td className="px-3 py-2 text-muted-foreground">{a.description || '—'}</td>
                <td className="px-3 py-2">{a.uploaded_by || a.entered_by || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{a.uploaded_at ? fmtTz(a.uploaded_at) : '—'}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${basePath}/attachments/${encodeURIComponent(a.safe_name || a.file_name)}/download`} target="_blank" rel="noreferrer" className="text-primary hover:opacity-70"><Download className="h-3.5 w-3.5" /></a>
                    {canEdit && <button onClick={() => handleDelete(a)} className="text-destructive hover:opacity-70"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// ── Terms & Conditions Panel ────────────────────────────────────────────────

export function TermsPanel({ docType, docNumber, canEdit, onReload, fmtTz }) {
  const confirm = useConfirm();
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const basePath = docType === 'orders' ? `/api/orders/${docNumber}` : `/api/requisitions/${docNumber}`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${basePath}/terms`);
      setTerms(Array.isArray(r.data) ? r.data : r.data?.terms || []);
    } catch (e) { console.error("Panel load failed:", e?.response?.status, e?.message); }
    setLoading(false);
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  const addTerm = async (code, record) => {
    if (!code) return;
    try {
      await api.post(`${basePath}/terms`, { code, description: record?.outline || record?.description || '' });
      toast.success('Terms added');
      setAdding(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeTerm = async (code) => {
    if (!await confirm('Remove these terms?', { title: 'Remove Terms & Conditions', destructive: true })) return;
    try {
      await api.delete(`${basePath}/terms/${encodeURIComponent(code)}`);
      toast.success('Terms removed');
      load();
    } catch { toast.error('Remove failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-primary">Terms & Conditions</h3>
        {canEdit && <Button size="sm" variant="outline" onClick={() => setAdding(!adding)}><Plus className="h-3.5 w-3.5 mr-1" /> Add Terms</Button>}
      </div>
      {adding && (
        <div className="mb-3 p-3 border rounded-lg bg-muted/20">
          <Label className="text-xs mb-1 block">Search Terms & Conditions:</Label>
          <TypeAheadLookup endpoint="/api/data/terms-conditions" valueField="code" labelField="outline" value="" displayValue="" placeholder="Type to search terms..." onChange={addTerm} excludeValues={terms.map(t => t.code)} />
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => setAdding(false)}><X className="h-3 w-3 mr-1" /> Cancel</Button>
        </div>
      )}
      {loading ? <p className="text-xs text-muted-foreground">Loading...</p> : terms.length === 0 ? (
        <EmptyState title="No terms & conditions" />
      ) : (
        <div className="space-y-2">
          {terms.map((t, i) => (
            <div key={i} className="flex items-start gap-3 p-3 border rounded-lg">
              <ScrollText className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">{t.code}</p>
                <p className="text-xs text-muted-foreground">{t.description || t.outline}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{t.added_by} — {t.added_at ? fmtTz(t.added_at) : ''}</p>
              </div>
              {canEdit && <button onClick={() => removeTerm(t.code)} className="text-destructive hover:opacity-70"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Notes Panel ─────────────────────────────────────────────────────────────

export function NotesPanel({ docType, docNumber, fmtTz }) {
  const confirm = useConfirm();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  const basePath = docType === 'orders' ? `/api/orders/${docNumber}` : `/api/requisitions/${docNumber}`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${basePath}/notes`);
      setNotes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error('Notes load failed:', e?.response?.status, e?.message);
    }
    setLoading(false);
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const payload = docType === 'orders' ? { note: newNote } : { notes: newNote };
      await api.post(`${basePath}/notes`, payload);
      toast.success('Note added');
      setNewNote('');
      load();
    } catch { toast.error('Failed to add note'); }
    setSaving(false);
  };

  const deleteNote = async (idx) => {
    if (!await confirm('Delete this note?', { title: 'Delete Note', destructive: true })) return;
    try {
      const noteId = notes[idx]?.id || idx;
      await api.delete(`${basePath}/notes/${noteId}`);
      toast.success('Note deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <h3 className="font-bold text-primary mb-3">Notes</h3>
      <div className="mb-3">
        <Textarea value={newNote} onChange={e => setNewNote(e.target.value)} rows={2} placeholder="Add a note..." className="text-sm mb-2" />
        <Button size="sm" onClick={addNote} disabled={saving || !newNote.trim()}>
          <StickyNote className="h-3.5 w-3.5 mr-1" /> {saving ? 'Adding...' : 'Add Note'}
        </Button>
      </div>
      {loading ? <p className="text-xs text-muted-foreground">Loading...</p> : notes.length === 0 ? (
        <EmptyState title="No notes yet" />
      ) : (
        <div className="space-y-2">
          {notes.map((n, i) => (
            <div key={i} className="flex items-start gap-2 p-3 border rounded-lg">
              <StickyNote className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm">{n.note || n.notes}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{n.created_by || n.entered_by} — {n.created_at || n.entry_date ? fmtTz(n.created_at || n.entry_date) : ''}</p>
              </div>
              <button onClick={() => deleteNote(i)} className="text-destructive hover:opacity-70 flex-shrink-0"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ── Cost Centre Splits Panel ────────────────────────────────────────────────

export function CostCentreSplitsPanel({ docType, docNumber, netTotal, fmtTz, onUpdate }) {
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const basePath = docType === 'orders' ? `/api/orders/${docNumber}` : `/api/requisitions/${docNumber}`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${basePath}/cost-centre-splits`);
      setSplits(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.error('CC splits load failed:', e?.message); }
    setLoading(false);
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  const addSplit = (code, record) => {
    if (!code) return;
    if (splits.find(s => s.cost_centre_code === code)) { toast.error('This cost centre is already added'); return; }
    const existing = splits.length;
    let newPct = existing === 0 ? 100 : 0;
    // Auto-distribute: if adding to existing splits, take from each equally
    const newSplits = [...splits];
    if (existing > 0) {
      const takeFromEach = Math.floor(100 / (existing + 1) * 100) / 100;
      newPct = 100;
      newSplits.forEach(s => {
        s.split_percentage = takeFromEach;
        newPct -= takeFromEach;
      });
      newPct = Math.round(newPct * 100) / 100;
    }
    newSplits.push({
      cost_centre_code: code,
      cost_centre_name: record?.description || code,
      split_percentage: newPct,
      split_value: Math.round((netTotal || 0) * newPct / 100 * 100) / 100,
    });
    setSplits(newSplits);
    setAdding(false);
  };

  const updatePercentage = (idx, newPct) => {
    newPct = Math.max(0, Math.min(100, parseFloat(newPct) || 0));
    const newSplits = [...splits];
    const oldPct = newSplits[idx].split_percentage;
    const diff = newPct - oldPct;
    newSplits[idx].split_percentage = newPct;

    // Auto-balance: distribute the difference across OTHER splits proportionally
    const others = newSplits.filter((_, i) => i !== idx);
    const othersTotal = others.reduce((s, o) => s + o.split_percentage, 0);

    if (others.length > 0 && othersTotal > 0) {
      others.forEach(o => {
        const share = (o.split_percentage / othersTotal) * (-diff);
        o.split_percentage = Math.max(0, Math.round((o.split_percentage + share) * 100) / 100);
      });
    } else if (others.length > 0) {
      // All others are 0, distribute equally
      const each = Math.round((100 - newPct) / others.length * 100) / 100;
      others.forEach(o => { o.split_percentage = each; });
    }

    // Recalculate values
    newSplits.forEach(s => { s.split_value = Math.round((netTotal || 0) * s.split_percentage / 100 * 100) / 100; });
    setSplits(newSplits);
  };

  const removeSplit = async (idx) => {
    const newSplits = splits.filter((_, i) => i !== idx);
    // Redistribute to remaining
    if (newSplits.length > 0) {
      const each = Math.round(100 / newSplits.length * 100) / 100;
      newSplits.forEach((s, i) => {
        s.split_percentage = i === newSplits.length - 1 ? 100 - each * (newSplits.length - 1) : each;
        s.split_value = Math.round((netTotal || 0) * s.split_percentage / 100 * 100) / 100;
      });
    }
    setSplits(newSplits);
    // Auto-save the deletion
    try {
      await api.put(`${basePath}/cost-centre-splits`, { splits: newSplits });
      toast.success('Cost centre removed');
      if (onUpdate) onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const totalPct = splits.reduce((s, sp) => s + sp.split_percentage, 0);
  const isBalanced = splits.length === 0 || Math.abs(totalPct - 100) < 0.01;

  const saveSplits = async () => {
    if (!isBalanced) { toast.error('Total must equal 100%'); return; }
    setSaving(true);
    try {
      await api.put(`${basePath}/cost-centre-splits`, { splits });
      toast.success('Cost centre splits saved');
      load();
      if (onUpdate) onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (loading) return <p className="text-xs text-muted-foreground">Loading...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-primary">Cost Centre Splits</h3>
        <Button size="sm" variant="outline" onClick={() => setAdding(!adding)} data-testid="add-cc-split">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Cost Centre
        </Button>
      </div>

      {adding && (
        <div className="mb-3 p-3 border rounded-lg bg-muted/20">
          <Label className="text-xs mb-1 block">Search Cost Centres:</Label>
          <TypeAheadLookup endpoint="/api/account-codes/7" valueField="code" labelField="description" value="" displayValue="" placeholder="Type to search cost centres..." onChange={addSplit} />
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => setAdding(false)}><X className="h-3 w-3 mr-1" /> Cancel</Button>
        </div>
      )}

      {splits.length === 0 ? (
        <EmptyState title="No cost centre splits" description="Add cost centres to split the document value across multiple budgets." />
      ) : (
        <div>
          <table className="w-full text-xs border-collapse mb-3">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-3 py-2">Cost Centre</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2" style={{ width: 100 }}>Split %</th>
                <th className="text-right px-3 py-2" style={{ width: 120 }}>Split Value</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {splits.map((s, i) => (
                <tr key={i} className="border-b hover:bg-accent/20">
                  <td className="px-3 py-2 font-mono text-primary font-semibold">{s.cost_centre_code}</td>
                  <td className="px-3 py-2 text-muted-foreground">{s.cost_centre_name}</td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" step="0.01" min="0" max="100" value={s.split_percentage}
                      onChange={e => updatePercentage(i, e.target.value)}
                      className="h-7 w-20 text-right text-xs ml-auto" />
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{s.split_value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="px-1"><button onClick={() => removeSplit(i)} className="text-destructive hover:opacity-70"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={`font-bold ${isBalanced ? 'bg-green-50' : 'bg-red-50'}`}>
                <td className="px-3 py-2" colSpan={2}>Total</td>
                <td className={`px-3 py-2 text-right ${isBalanced ? 'text-green-700' : 'text-red-700'}`}>{totalPct.toFixed(2)}%</td>
                <td className="px-3 py-2 text-right font-mono">{splits.reduce((s, sp) => s + (sp.split_value || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>

          {!isBalanced && (
            <p className="text-xs text-red-500 mb-2">Total must equal 100%. Currently {totalPct.toFixed(2)}%. Adjust percentages to balance.</p>
          )}

          <Button size="sm" onClick={saveSplits} disabled={saving || !isBalanced} data-testid="save-cc-splits">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Splits'}
          </Button>
        </div>
      )}
    </div>
  );
}
